import type { CellObject, WorkBook } from 'xlsx';

import { CATEGORY_EXPORT_LABELS } from './categories';
import { localIsoDate } from './date-format';
import type { Cents, LineItem, Receipt } from './types';

/**
 * Shared Excel workbook builder used by both exporters:
 *   - `lib/export-xlsx.ts` for the standalone .xlsx download
 *   - `lib/export-zip.ts` for the `receipts.xlsx` packed inside the ZIP
 *
 * Both produce the same workbook shape: a "Summary" sheet listing every
 * receipt with the headline fields + a Grand Total row per currency,
 * followed by one breakdown sheet per receipt (header + items table +
 * totals). The only thing that differs between the two callers is what
 * goes in the Summary sheet's "Filename" column — the standalone xlsx
 * uses the source upload filename, the ZIP uses the in-archive JPG
 * filename so the user can locate the matching image. Each caller fills
 * `displayName` accordingly.
 *
 * Low-quality-photo receipts still appear as rows on the Summary sheet
 * and get their own breakdown sheet — the user wants to see and fix
 * them — but they're held out of the Grand Total because their parsed
 * totals can't be trusted (the same exclusion runs in BatchProgress).
 */

const MONEY_FORMAT = '#,##0.00';
const QTY_FORMAT = '0.###';

export interface WorkbookItem {
  receipt: Receipt;
  /** Cell content for the Summary sheet's "Filename" column. */
  displayName: string;
  /**
   * Sheet name for the per-receipt breakdown. Excel caps these at 31 chars
   * and silently truncates duplicates to the same name, so the caller is
   * responsible for sanitising + deduping before passing them in.
   */
  sheetName: string;
  /** True when the session was flagged `low_quality_photo` post-validate. */
  lowQuality: boolean;
}

function centsToNumber(cents: Cents): number {
  return cents / 100;
}

function moneyCell(cents: Cents): CellObject {
  return { t: 'n', v: centsToNumber(cents), z: MONEY_FORMAT };
}

function optionalMoneyCell(cents: Cents | undefined): CellObject | string {
  return cents === undefined ? '' : moneyCell(cents);
}

function qtyCell(qty: number): CellObject {
  return { t: 'n', v: qty, z: QTY_FORMAT };
}

type Cell = string | CellObject;

/**
 * Sum receipt totals grouped by currency. Cross-currency sums aren't
 * meaningful so each currency gets its own row. Insertion order
 * preserved so the dominant currency in a typical single-currency batch
 * shows up first.
 */
function totalsByCurrency(receipts: readonly Receipt[]): Map<string, Cents> {
  const totals = new Map<string, Cents>();
  for (const r of receipts) {
    const ccy = r.currency.value;
    totals.set(ccy, (totals.get(ccy) ?? 0) + r.total.value);
  }
  return totals;
}

function summarySheetRows(items: readonly WorkbookItem[]): Cell[][] {
  const rows: Cell[][] = [
    [
      'Filename',
      'Vendor',
      'Date',
      'Payment',
      'Category',
      'Tags',
      'Notes',
      'Total',
      'Currency',
    ],
  ];
  for (const it of items) {
    rows.push([
      it.displayName,
      it.receipt.vendor.value,
      localIsoDate(it.receipt.date.value),
      it.receipt.paymentMethod?.value ?? '',
      it.receipt.category !== undefined
        ? CATEGORY_EXPORT_LABELS[it.receipt.category]
        : '',
      it.receipt.tags !== undefined ? it.receipt.tags.join(', ') : '',
      it.receipt.notes ?? '',
      moneyCell(it.receipt.total.value),
      it.receipt.currency.value,
    ]);
  }
  const totals = totalsByCurrency(
    items.filter((it) => !it.lowQuality).map((it) => it.receipt),
  );
  if (totals.size > 0) {
    rows.push([]);
    for (const [ccy, sum] of totals) {
      rows.push(['Grand Total', '', '', '', '', '', '', moneyCell(sum), ccy]);
    }
  }
  return rows;
}

function breakdownSheetRows(item: WorkbookItem, excludeTax: boolean): Cell[][] {
  const r = item.receipt;
  const rows: Cell[][] = [
    ['Vendor', r.vendor.value],
    ['Date', localIsoDate(r.date.value)],
    ['Currency', r.currency.value],
    ['Payment', r.paymentMethod?.value ?? ''],
    [
      'Category',
      r.category !== undefined ? CATEGORY_EXPORT_LABELS[r.category] : '',
    ],
    ['Tags', r.tags !== undefined ? r.tags.join(', ') : ''],
    ['Notes', r.notes ?? ''],
    [],
    ['Item', 'Qty', 'Unit Price', 'Line Total'],
  ];
  for (const li of r.items as LineItem[]) {
    rows.push([
      li.name.value,
      qtyCell(li.qty.value),
      optionalMoneyCell(li.unitPrice?.value),
      moneyCell(li.total.value),
    ]);
  }
  rows.push([]);
  // Subtotal and Tax are dropped together when excludeTax is set —
  // total − subtotal would expose the tax amount otherwise. Tip is
  // independent of tax and stays.
  if (!excludeTax) {
    rows.push(
      ['Subtotal', '', '', optionalMoneyCell(r.subtotal?.value)],
      ['Tax', '', '', optionalMoneyCell(r.tax?.value)],
    );
  }
  rows.push(
    ['Tip', '', '', optionalMoneyCell(r.tip?.value)],
    ['Total', '', '', moneyCell(r.total.value)],
  );
  return rows;
}

export interface BuildExcelOptions {
  excludeTax?: boolean;
}

export async function buildExcelBlob(
  items: readonly WorkbookItem[],
  opts: BuildExcelOptions = {},
): Promise<Blob> {
  const excludeTax = opts.excludeTax ?? false;
  const XLSX = await import('xlsx');
  const wb: WorkBook = XLSX.utils.book_new();

  // `aoa_to_sheet` is typed `any[][]` in xlsx — cast at the call site.
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(summarySheetRows(items) as Cell[][]),
    'Summary',
  );
  for (const item of items) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(breakdownSheetRows(item, excludeTax) as Cell[][]),
      item.sheetName,
    );
  }

  const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
